import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { AccountSettingsPayload } from '@account-users/account/models/AccountSettingsPayload';
import { FormState } from '@shared';
import { Permission } from '@account-users/account/models/Permission';

interface ValidationMessages {
  required?: string;
  maxValue?: string;
  minValue?: string;
}

@Component({
  selector: 'app-number-order-field',
  templateUrl: './number-order-field.component.html',
  styleUrls: ['./number-order-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberOrderFieldComponent implements OnInit, OnChanges {
  @Input() title = false;
  @Input() leftFieldlabel = '';
  @Input() leftFieldPlaceholder = '';
  @Input() errorTextMin = '';
  @Input() buttonTooltip = '';
  @Input() valueToUpdate = '';
  @Input() userPermissions: Permission[] = [];
  @Input() isUpdatingInput: boolean;
  @Input() inputValue: number;
  @Input() maxValue = 1000000;
  @Input() set validationErrorMessages(
    messages: ValidationMessages | undefined,
  ) {
    this.validationMessages = {
      required: messages?.required || this.validationMessages.required,
      maxValue: messages?.maxValue || this.validationMessages.maxValue,
      minValue: messages?.minValue || this.validationMessages.minValue,
    };
  }
  @Output() editInput = new EventEmitter<AccountSettingsPayload>();

  form: FormGroup;
  FormState = FormState;

  validationMessages: ValidationMessages;

  constructor(private fb: FormBuilder) {
    this.validationMessages = {
      required: 'Field is required',
      maxValue: 'Too large value',
      minValue: 'Too less value',
    };
  }

  ngOnInit() {
    this.form = this.fb.group({
      [this.valueToUpdate]: this.fb.control(
        { value: this.inputValue, disabled: !this.canPlaceOrders },
        [Validators.required, Validators.min(0), Validators.max(this.maxValue)],
      ),
      orderId: this.fb.control({ value: '', disabled: !this.canPlaceOrders }, [
        Validators.required,
        Validators.pattern(/^[A-Za-z0-9]{16}$/),
      ]),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const inputValue = changes['inputValue'];
    if (
      inputValue?.currentValue &&
      !inputValue.firstChange &&
      this.valueToUpdate
    ) {
      this.form.patchValue({ [this.valueToUpdate]: inputValue.currentValue });
    }
  }

  get value() {
    return this.form.get(this.valueToUpdate) as FormControl;
  }

  get orderId() {
    return this.form.get('orderId') as FormControl;
  }

  get disabledSubmit() {
    return (
      !this.canPlaceOrders ||
      this.isUpdatingInput ||
      this.form.invalid ||
      this.value.value === this.inputValue
    );
  }

  get canPlaceOrders() {
    return this.userPermissions.includes(Permission.CanPlaceOrders);
  }

  onSubmit($event: Event) {
    $event.preventDefault();

    if (this.form.valid && this.value.touched) {
      this.editInput.emit({
        [this.valueToUpdate]: this.value.value,
        orderId: this.orderId.value,
      });

      this.orderId.reset();
    }
  }
}
